/**
 * R3-Phase-3 — operator-facing invitation list per Run.
 *
 * Shown beneath the "Share with viewers" button on /runs/:run_id.
 * Lets the operator see who has been invited, the per-invitation
 * status (sent / opened / viewed / expired / revoked), view count,
 * last-viewed timestamp, and revoke or resend each invitation.
 */
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Loader2,
  Mail,
  RefreshCw,
  ShieldOff,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { api, formatApiError } from "@/lib/api";

const STATUS_PILL = {
  pending: "bg-slate-100 text-slate-700 border-slate-200",
  sent: "bg-indigo-50 text-indigo-700 border-indigo-200",
  opened: "bg-blue-50 text-blue-700 border-blue-200",
  viewed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  expired: "bg-slate-100 text-slate-500 border-slate-200",
  revoked: "bg-rose-50 text-rose-700 border-rose-200",
};

const STATUS_LABEL = {
  pending: "Pending",
  sent: "Sent",
  opened: "Opened",
  viewed: "Viewed",
  expired: "Expired",
  revoked: "Revoked",
};
function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusPill({ status }) {
  const cls = STATUS_PILL[status] || STATUS_PILL.pending;
  const label = STATUS_LABEL[status] || status;
  return (
    <span
      data-testid={`invitation-status-${status}`}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}
    >
      {label}
    </span>
  );
}

export default function InvitationsTable({ runId }) {
  const queryClient = useQueryClient();
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [resendTarget, setResendTarget] = useState(null);

  const listQuery = useQuery({
    queryKey: ["run-invitations", runId],
    queryFn: async () =>
      (await api.get(`/demo-runs/${runId}/invitations`)).data,
    refetchInterval: 15_000,
  });

  const revokeMutation = useMutation({
    mutationFn: async (invitationId) =>
      (await api.delete(`/invitations/${invitationId}`)).data,
    onSuccess: () => {
      toast.success("Invitation revoked");
      queryClient.invalidateQueries({ queryKey: ["run-invitations", runId] });
    },
    onError: (err) => toast.error(formatApiError(err)),
    onSettled: () => setRevokeTarget(null),
  });

  const resendMutation = useMutation({
    mutationFn: async (invitationId) =>
      (await api.post(`/invitations/${invitationId}/resend`)).data,
    onSuccess: (data) => {
      if (data?.status === "sent") {
        toast.success(`Invitation resent to ${data.email}`);
      } else {
        toast.error(`Resend failed: ${data?.error || "unknown error"}`);
      }
      queryClient.invalidateQueries({ queryKey: ["run-invitations", runId] });
    },
    onError: (err) => toast.error(formatApiError(err)),
    onSettled: () => setResendTarget(null),
  });

  const items = listQuery.data?.items || [];

  return (
    <div data-testid="invitations-table" className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Mail size={14} className="text-indigo-600" />
          Invitations · {items.length}
        </h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() =>
            queryClient.invalidateQueries({
              queryKey: ["run-invitations", runId],
            })
          }
          data-testid="invitations-refresh"
          className="h-7 gap-1 px-2 text-[11px] text-slate-500 hover:text-slate-900"
        >
          <RefreshCw size={12} /> Refresh
        </Button>
      </div>

      {listQuery.isLoading ? (
        <div className="flex items-center gap-2 py-4 text-xs text-slate-500" data-testid="invitations-loading">
          <Loader2 size={12} className="animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <p
          className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500"
          data-testid="invitations-empty"
        >
          No invitations yet. Click <strong>Share with viewers</strong> to invite someone.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="invitations-table-rows">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="py-2 pr-3 font-semibold">Email</th>
                <th className="py-2 pr-3 font-semibold">Status</th>
                <th className="py-2 pr-3 font-semibold">Expires</th>
                <th className="py-2 pr-3 font-semibold">Views</th>
                <th className="py-2 pr-3 font-semibold">Last viewed</th>
                <th className="py-2 pr-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((inv) => {
                const terminal = inv.status === "revoked" || inv.status === "expired";
                return (
                  <tr
                    key={inv.invitation_id}
                    className="border-b border-slate-100 last:border-b-0"
                    data-testid={`invitation-row-${inv.invitation_id}`}
                  >
                    <td className="py-2 pr-3 align-top">
                      <span className="font-mono text-xs text-slate-800">{inv.invited_email}</span>
                      {inv.invited_company_name ? (
                        <div className="text-[11px] text-slate-500">{inv.invited_company_name}</div>
                      ) : null}
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <StatusPill status={inv.status} />
                    </td>
                    <td className="py-2 pr-3 align-top text-xs text-slate-600">
                      {formatDate(inv.expires_at)}
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <span
                        data-testid={`invitation-views-${inv.invitation_id}`}
                        className="font-mono text-xs text-slate-700"
                      >
                        {inv.view_count}
                      </span>
                    </td>
                    <td className="py-2 pr-3 align-top text-xs text-slate-600">
                      {formatDate(inv.last_viewed_at)}
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <div className="flex flex-wrap items-center gap-1">
                        {!terminal ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setResendTarget(inv)}
                            data-testid={`invitation-resend-${inv.invitation_id}`}
                            className="h-7 gap-1 px-2 text-[11px] text-indigo-700 hover:bg-indigo-50"
                          >
                            <RefreshCw size={11} />
                            Resend
                          </Button>
                        ) : null}
                        {inv.status !== "revoked" ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setRevokeTarget(inv)}
                            data-testid={`invitation-revoke-${inv.invitation_id}`}
                            className="h-7 gap-1 px-2 text-[11px] text-rose-700 hover:bg-rose-50"
                          >
                            <ShieldOff size={11} />
                            Revoke
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog
        open={revokeTarget !== null}
        onOpenChange={(v) => {
          if (!v) setRevokeTarget(null);
        }}
      >
        <AlertDialogContent data-testid="invitation-revoke-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke access?</AlertDialogTitle>
            <AlertDialogDescription>
              Revoke access for <strong>{revokeTarget?.invited_email}</strong>?
              They will immediately lose access to the video.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="invitation-revoke-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="invitation-revoke-confirm"
              onClick={() => revokeMutation.mutate(revokeTarget.invitation_id)}
              className="bg-rose-600 hover:bg-rose-500"
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={resendTarget !== null}
        onOpenChange={(v) => {
          if (!v) setResendTarget(null);
        }}
      >
        <AlertDialogContent data-testid="invitation-resend-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Resend invitation?</AlertDialogTitle>
            <AlertDialogDescription>
              Resend invitation to <strong>{resendTarget?.invited_email}</strong>?
              The previous link will stop working.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="invitation-resend-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="invitation-resend-confirm"
              onClick={() => resendMutation.mutate(resendTarget.invitation_id)}
              className="bg-indigo-600 hover:bg-indigo-500"
            >
              Resend
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
